// Operator alerting — admin-only.
//
// Backend routes under /v1/admin/alerts: the recipient list (who gets
// mailed when a payout rail stops answering), the transport in use, the
// last settled state per rail, a test send and a check-now.
//
// States carry only the provider key — the panel names rails through
// providerLabel(), never the upstream host.
import { apiClient } from "./client";

export interface AlertRecipient {
  id: number;
  email: string;
  enabled: boolean;
  created_at: string;
}

export interface AlertChannel {
  id: number;
  name: string;
  /** "webhook" (Workflow URL) or "graph" (posted as the connected account). */
  kind: "webhook" | "graph";
  /** Masked webhook (host + tail) — the full URL never comes back. */
  webhook_ref: string;
  enabled: boolean;
  created_at: string;
}

export interface AlertRailState {
  provider: string;
  status: "ok" | "down";
  detail: string;
  since: string;
  last_checked_at: string;
  last_notified_at: string | null;
}

export interface AlertsOverview {
  recipients: AlertRecipient[];
  /** Who the next mail would actually go to: enabled rows, or the
   *  deployment's fallback list while no rows exist. */
  effective_recipients: string[];
  /** Teams channels (Workflow webhooks) that also get every alert. */
  channels: AlertChannel[];
  /** "graph" | "postal" | "none" */
  mailer: string;
  states: AlertRailState[];
}

export async function getAlertsOverview(): Promise<AlertsOverview> {
  const resp = await apiClient.get<AlertsOverview>("/v1/admin/alerts");
  // Defensive: an empty Go slice can arrive as null, and every consumer
  // reads .length / .map off these.
  const d = resp.data;
  return {
    ...d,
    recipients: d.recipients ?? [],
    effective_recipients: d.effective_recipients ?? [],
    channels: d.channels ?? [],
    states: d.states ?? [],
  };
}

export async function addAlertRecipient(email: string): Promise<AlertRecipient> {
  const resp = await apiClient.post<AlertRecipient>("/v1/admin/alerts/recipients", { email });
  return resp.data;
}

export async function setAlertRecipientEnabled(id: number, enabled: boolean): Promise<AlertRecipient> {
  const resp = await apiClient.patch<AlertRecipient>(`/v1/admin/alerts/recipients/${id}`, { enabled });
  return resp.data;
}

export async function deleteAlertRecipient(id: number): Promise<void> {
  await apiClient.delete(`/v1/admin/alerts/recipients/${id}`);
}

export async function addAlertChannel(name: string, webhookUrl: string): Promise<AlertChannel> {
  const resp = await apiClient.post<AlertChannel>("/v1/admin/alerts/channels", { name, webhook_url: webhookUrl });
  return resp.data;
}

export async function setAlertChannelEnabled(id: number, enabled: boolean): Promise<AlertChannel> {
  const resp = await apiClient.patch<AlertChannel>(`/v1/admin/alerts/channels/${id}`, { enabled });
  return resp.data;
}

export async function deleteAlertChannel(id: number): Promise<void> {
  await apiClient.delete(`/v1/admin/alerts/channels/${id}`);
}

export async function sendAlertTest(): Promise<{ sent_to: string[]; mailer: string }> {
  const resp = await apiClient.post<{ sent_to: string[]; mailer: string }>("/v1/admin/alerts/test");
  return resp.data;
}

export async function runAlertCheck(): Promise<AlertsOverview> {
  const resp = await apiClient.post<AlertsOverview>("/v1/admin/alerts/check");
  return resp.data;
}

// ─── Teams account (posts as the Strata account) ─────────────────────────────

export interface TeamsAccount {
  upn: string;
  display_name: string;
  connected_at: string;
  last_refreshed_at: string | null;
  /** Set when the last token refresh failed — the account needs reconnecting. */
  last_error: string | null;
}

export interface TeamsPickerTeam {
  id: string;
  name: string;
  channels: { id: string; name: string }[];
}

export interface TeamsPickerChat {
  id: string;
  name: string;
  type: string;
}

export interface TeamsOverview {
  account: TeamsAccount | null;
  teams: TeamsPickerTeam[];
  chats: TeamsPickerChat[];
}

export async function getTeamsOverview(): Promise<TeamsOverview> {
  const resp = await apiClient.get<TeamsOverview>("/v1/admin/alerts/teams");
  const d = resp.data;
  return {
    account: d.account ?? null,
    teams: (d.teams ?? []).map((t) => ({ ...t, channels: t.channels ?? [] })),
    chats: d.chats ?? [],
  };
}

export interface DeviceCodeStart {
  id: string;
  user_code: string;
  verification_url: string;
  expires_in: number;
  interval: number;
}

export async function startTeamsConnect(): Promise<DeviceCodeStart> {
  const resp = await apiClient.post<DeviceCodeStart>("/v1/admin/alerts/teams/connect");
  return resp.data;
}

/** Resolves "pending" (202) or "connected" (200). */
export async function pollTeamsConnect(id: string): Promise<"pending" | "connected"> {
  const resp = await apiClient.post<{ status: string }>(`/v1/admin/alerts/teams/connect/${id}`);
  return resp.status === 202 ? "pending" : "connected";
}

export async function disconnectTeams(): Promise<void> {
  await apiClient.delete("/v1/admin/alerts/teams");
}

export async function addAlertGraphChannel(name: string, teamId: string, channelId: string): Promise<AlertChannel> {
  const resp = await apiClient.post<AlertChannel>("/v1/admin/alerts/channels", {
    name,
    team_id: teamId,
    channel_id: channelId,
  });
  return resp.data;
}
